import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import i18n from "i18next";

/**
 * Picks the i18n language for the public anonymized talent page from a `lang=`
 * URL query parameter, with fr → en → fallback.
 *
 * This page is reached without any user session, so we cannot rely on the
 * localStorage-cached language or on the user's profile. The CSM picks the
 * language at "copy URL" time (FR vs EN button), encoding their choice in the
 * URL — the prospect's browser then auto-switches as soon as the page mounts.
 *
 * Returns the resolved language and a setter the language-switcher widget on
 * the public page can wire to (keeps the URL in sync when the visitor flips
 * the toggle on the fly).
 */
const SUPPORTED = ["fr", "en"] as const;
type SupportedLanguage = (typeof SUPPORTED)[number];

function normalize(lang: string | null | undefined): SupportedLanguage {
	const lower = (lang ?? "").toLowerCase();
	return (SUPPORTED as readonly string[]).includes(lower)
		? (lower as SupportedLanguage)
		: "fr";
}

export function usePublicLanguageSwitch(): {
	language: SupportedLanguage;
	setLanguage: (lang: SupportedLanguage) => void;
} {
	const [searchParams, setSearchParams] = useSearchParams();
	const initial = normalize(searchParams.get("lang"));
	const [language, setLanguageState] = useState<SupportedLanguage>(initial);

	useEffect(() => {
		// Apply the language to i18next on mount and on every URL change. Doing it
		// in an effect (and not eagerly during render) keeps the public page safe
		// in test / SSR contexts where i18next may not be fully ready yet.
		if (i18n.language !== language) {
			i18n.changeLanguage(language);
		}
	}, [language]);

	// Memoize so consumers (effects, child components) can safely use the
	// setter as a dependency without re-firing on every render.
	const setLanguage = useCallback(
		(lang: SupportedLanguage) => {
			setLanguageState(lang);
			const next = new URLSearchParams(searchParams);
			next.set("lang", lang);
			setSearchParams(next, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	return useMemo(
		() => ({ language, setLanguage }),
		[language, setLanguage]
	);
}
